// @flow
import { handleActions } from "redux-actions";
import { APP_NAMESPACE } from "shared/constants/application";
import { ERROR, LOADED, LOADING, NONE } from "shared/constants/status";
import { LOGOUT } from "redux/modules/auth";
import { GET_ME_REQUEST } from "redux/modules/me/meActions";
import type { BusinessLocationRecord } from "redux/modules/createCabelasClass/records/BusinessLocationRecord";
import { isChapterLocation } from "redux/modules/businessEventScheduling/instructorLocations";

const namespace = `${APP_NAMESPACE}/instructorChapters`;
export const REQUEST: string = `${namespace}/LOCATIONS/REQUEST`;
export const RECEIVE: string = `${namespace}/LOCATIONS/SUCCESS`;
export const REQUEST_ERROR: string = `${namespace}/LOCATIONS/ERROR`;
export const SELECT: string = `${namespace}/SELECT`;

export type State = {
  status: string,
  errorMessage: string,
  userId: string,
  requestId: number,
  locations: Array<BusinessLocationRecord>,
  selectedLocationId: string,
};

type RequestIdentity = { userId: string, requestId: number };
export type ChapterLocationsAction = {
  type: string,
  payload: RequestIdentity & {
    locations?: Array<BusinessLocationRecord>,
    errorMessage?: string,
    locationId?: string,
  },
};

let requestSequence = 0;
export const getChapterLocations = (
  userId: string
): ChapterLocationsAction => ({
  type: REQUEST,
  payload: { userId, requestId: ++requestSequence },
});
export const receiveChapterLocations = (
  locations: Array<BusinessLocationRecord>,
  identity: RequestIdentity
): ChapterLocationsAction => ({
  type: RECEIVE,
  payload: { ...identity, locations },
});
export const chapterLocationsError = (
  errorMessage: string,
  identity: RequestIdentity
): ChapterLocationsAction => ({
  type: REQUEST_ERROR,
  payload: { ...identity, errorMessage },
});
export const selectChapterLocation = (locationId: string): mixed => ({
  type: SELECT,
  payload: { locationId },
});

const initialState: State = {
  status: NONE,
  errorMessage: "",
  userId: "",
  requestId: 0,
  locations: [],
  selectedLocationId: "",
};
const isCurrentRequest = (
  state: State,
  action: ChapterLocationsAction
): boolean =>
  state.status === LOADING &&
  state.userId === action.payload.userId &&
  state.requestId === action.payload.requestId;

export const hasChapterLocations = (state: State, userId: ?string): boolean =>
  !!userId &&
  state.status === LOADED &&
  state.userId === userId &&
  state.locations.some(isChapterLocation);

export const getSelectedChapterLocation = (
  state: State
): ?BusinessLocationRecord =>
  state.status === LOADED
    ? state.locations.find(
        (location: BusinessLocationRecord): boolean =>
          location.locationId === state.selectedLocationId
      )
    : null;

export default handleActions(
  {
    [REQUEST]: (state: State, { payload }: ChapterLocationsAction): State => ({
      ...initialState,
      status: LOADING,
      userId: payload.userId,
      requestId: payload.requestId,
    }),
    [RECEIVE]: (state: State, action: ChapterLocationsAction): State => {
      if (!isCurrentRequest(state, action)) {
        return state;
      }
      const locations = (action.payload.locations || []).filter(
        isChapterLocation
      );
      return {
        ...state,
        status: LOADED,
        locations,
        selectedLocationId:
          locations.length === 1 ? locations[0].locationId : "",
      };
    },
    [REQUEST_ERROR]: (state: State, action: ChapterLocationsAction): State =>
      isCurrentRequest(state, action)
        ? {
            ...state,
            status: ERROR,
            errorMessage:
              action.payload.errorMessage || "Unable to load your chapters.",
            locations: [],
            selectedLocationId: "",
          }
        : state,
    [SELECT]: (state: State, action: ChapterLocationsAction): State => {
      const location =
        state.status === LOADED &&
        state.locations.find(
          (item: BusinessLocationRecord): boolean =>
            item.locationId === action.payload.locationId
        );
      return {
        ...state,
        selectedLocationId: location ? location.locationId : "",
      };
    },
    [GET_ME_REQUEST]: (): State => initialState,
    [LOGOUT]: (): State => initialState,
  },
  initialState
);
